// Design Partner Experience Readiness — Part 1: the honest "Martian is
// learning" state.
//
// Root cause traced live during the Design Partner Readiness Audit: the
// AF-2.x pipeline (SignalService -> DiagnosisService -> OpportunityService
// -> RecommendationService -> ActionPlanService) only ever produces a
// signal when a real SoM check's score genuinely CHANGES from the prior
// one (SignalService.detect_signals()'s own meaningful-change gate). A
// brand-new client — or any client whose score simply hasn't moved yet —
// has a real, correctly-empty AF-2.x pipeline, which SahibBriefingPage
// already handled honestly (`now-baseline-card`) but with two generic
// lines that never explained WHY, what Martian is actually doing about it,
// or what to do next.
//
// This component only receives already-fetched, real data as props — no
// new API call, no new backend route, no duplicated intelligence logic.
// Every field it reads was already being fetched by SahibBriefingPage for
// its own existing sections (company context chip, "What Martian checked"'s
// prompt count, checkInProgress) and is only newly *arranged* here to
// answer this batch's five required questions honestly.
import { AskMartianButton } from '../AskMartianButton';
import { GLIMState } from '@/brand';

export default function MartianLearningState({
  brandLabel,
  companyContextSummaryText, // real, already-derived companyContextChip text, or null if nothing set
  promptsCount, // real prompts?.length, or null while still loading
  checkInProgress,
  onAskMartian,
  onReviewCompanyContext,
}) {
  const hasContext = Boolean(companyContextSummaryText);
  const hasChecked = typeof promptsCount === 'number' && promptsCount > 0;

  return (
    <div className="border-t-2 border-[var(--martian-rule,var(--border))] pt-4" data-testid="martian-learning-state">
      <div className="flex items-start gap-2 mb-2">
        <GLIMState state={checkInProgress ? 'discovering' : 'learning'} size={48} showLabel={false} className="shrink-0" />
        <p className="font-display text-[20px] font-bold text-[var(--martian-ink,var(--text-primary))] leading-tight" data-testid="martian-learning-headline">
          {checkInProgress ? 'Martian is checking now.' : 'Martian is still learning about your business.'}
        </p>
      </div>

      {/* 1. What Martian already knows -- real, or an honest "not yet". */}
      <p className="text-[13px] text-[var(--martian-ink-muted,var(--text-secondary))] leading-relaxed mb-1.5" data-testid="martian-learning-knows">
        {hasContext
          ? <>What Martian knows: <span className="text-[var(--martian-ink,var(--text-primary))]">{companyContextSummaryText}</span>.</>
          : <>Martian doesn't have a company profile for {brandLabel} yet — nothing here is invented until you or a discovery check provide one.</>}
      </p>

      {/* 2. What Martian is currently observing -- real prompt count / check state. */}
      <p className="text-[13px] text-[var(--martian-ink-muted,var(--text-secondary))] leading-relaxed mb-1.5" data-testid="martian-learning-observing">
        {promptsCount === null
          ? "What Martian is observing: loading your check history…"
          : hasChecked
            ? <>What Martian is observing: {promptsCount} real AI-visibility {promptsCount === 1 ? 'check has' : 'checks have'} run so far.</>
            : "What Martian is observing: no AI-visibility checks have run yet."}
      </p>

      {/* 3. Why deeper intelligence may not yet exist -- the real, specific
           mechanism, not a vague "check back later". */}
      <p className="text-[13px] text-[var(--martian-ink-muted,var(--text-secondary))] leading-relaxed mb-1.5" data-testid="martian-learning-why">
        Signals, diagnoses, opportunities, and recommendations are only generated once your Share of Model score genuinely changes between two checks — that hasn't happened yet, so there's honestly nothing deeper to show.
      </p>

      {/* 4. What happens next. */}
      <p className="text-[13px] text-[var(--martian-ink-muted,var(--text-secondary))] leading-relaxed mb-3" data-testid="martian-learning-next">
        The next time your score moves, a real signal — and the diagnosis and recommendation it produces — will appear here automatically. No action is required for this to happen.
      </p>

      {/* 5. A useful action, in both directions this batch asked for. */}
      <div className="flex items-center gap-4 flex-wrap">
        <AskMartianButton onClick={onAskMartian} />
        <button
          type="button"
          onClick={onReviewCompanyContext}
          data-testid="martian-learning-review-context"
          className="inline-flex items-center gap-1 text-[11px] font-medium text-[var(--martian-ink-muted,var(--text-tertiary))] hover:text-[var(--martian-violet,var(--accent))]"
        >
          {hasContext ? 'Review Company Context' : 'Add Company Context'}
        </button>
      </div>
    </div>
  );
}
